import { BudgetCategoryGroupDetailDrawer } from '@/components/cashflow/budget/BudgetCategoryGroupDetailDrawer';
import { BudgetPurposeDetailDrawer } from '@/components/cashflow/budget/BudgetPurposeDetailDrawer';
import Drawer, { DRAWER_BODY_ID } from '@/components/common/Drawer';
import FinancialEntityDetailView from '@/components/financialEntities/FinancialEntityDetailView';
import GoalDetailDrawer from '@/components/goals/detail/GoalDetailDrawer';
import GoalAccountDetailDrawer from '@/components/goals/GoalAccountDetailDrawer';
import { useTracking } from '@/services/analytics/useTracking';
import { createContext, FC, useCallback, useContext, useState } from 'react';

type DrawerProps = {
    id: string;
    onClose: VoidFunction;
};

type DrawerKey =
    | 'goal-detail'
    | 'goal-account-detail'
    | 'financial-entity-detail'
    | 'budget-purpose-detail'
    | 'budget-category-group-detail';

const DRAWERS: {
    [key in DrawerKey]: FC<DrawerProps>;
} = {
    'goal-detail': GoalDetailDrawer,
    'goal-account-detail': GoalAccountDetailDrawer,
    'financial-entity-detail': FinancialEntityDetailView,
    'budget-purpose-detail': BudgetPurposeDetailDrawer,
    'budget-category-group-detail': BudgetCategoryGroupDetailDrawer,
};

function isValidDrawerKey(key: string | null): key is DrawerKey {
    return key !== null && key in DRAWERS;
}

export type ShowDrawerFcn = (drawerKey: {
    drawerKey: DrawerKey;
    entityId: string;
    trapFocus?: boolean;
}) => void;

type DrawerManagerContext = {
    showDrawer: ShowDrawerFcn;
    hideDrawer: () => void;
};

export const DrawerManagerContext = createContext<DrawerManagerContext>({
    showDrawer: () => {},
    hideDrawer: () => {},
});

export const useDrawer = (): DrawerManagerContext => {
    return useContext(DrawerManagerContext);
};

const DrawerManager = ({ children }: { children: React.ReactNode }) => {
    const [drawerKey, setDrawerKey] = useState<DrawerKey | null>(null);
    const [entityId, setEntityId] = useState<string | null>(null);
    const [trapFocus, setTrapFocus] = useState<boolean>(true);
    const { track } = useTracking();

    const showDrawer: ShowDrawerFcn = useCallback(
        ({ drawerKey, entityId, trapFocus = true }) => {
            track('Drawer Opened', { drawerKey });
            setDrawerKey(drawerKey);
            setEntityId(entityId);
            setTrapFocus(trapFocus);

            const el = document.getElementById(DRAWER_BODY_ID);
            if (el) {
                el.scrollTo({ top: 0, behavior: 'smooth' });
            }
        },
        [setDrawerKey, setEntityId, track]
    );

    const hideDrawer = useCallback(() => {
        setDrawerKey(null);
        setEntityId(null);
        setTrapFocus(true);
    }, [setDrawerKey, setEntityId]);

    const CurrentDrawer = isValidDrawerKey(drawerKey) && DRAWERS[drawerKey];

    return (
        <DrawerManagerContext.Provider
            value={{
                showDrawer,
                hideDrawer,
            }}
        >
            {children}
            <Drawer
                isOpen={!!CurrentDrawer}
                onClose={hideDrawer}
                size="xl"
                trapFocus={trapFocus}
            >
                {CurrentDrawer && entityId && (
                    <CurrentDrawer id={entityId} onClose={hideDrawer} />
                )}
            </Drawer>
        </DrawerManagerContext.Provider>
    );
};

export default DrawerManager;
